import { degrees, PDFDocument, PDFName } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { EventBus, Events } from "../eventBus";
import { makeAnchorImage } from "./anchors";
import {
  METADATA_PRODUCER_NAME_FULL,
  OUTPUT_FILENAME_PREFIX,
} from "./constants";
import {
  findAnchorsInPageDiv,
  initialPageRender,
  renderAnchorPreview,
  renderPage,
  rescalePageContainer,
  fitDocumentContainerAroundPages,
  renderThumbnail,
  showAnchorTabOnThumbnail,
  hideAnchorTabOnThumbnail,
  resetPreviewThumbnail,
  getCurrentScale,
} from "./renderer";
import { createThumbnailImageFromRenderedCanvas } from "./thumbnails";
import {
  wayfinderGetCurrentLocation,
  wayfinderScrollToLocation,
} from "./wayfinder";
import {
  createXMPMetadata,
  parseXMPMetadata,
  xmlContainsQwilAnchors,
} from "./xmp";
import { getAutofitScale } from "./zoom";

let state = null;

export async function loadPdf(pdfSrc) {
  await destroyIfAlreadyLoaded();
  return new Promise(function (resolve, reject) {
    pdfjsLib.getDocument(pdfSrc).promise.then(
      function (pdf) {
        console.debug(`Loaded PDF with ${pdf.numPages} pages`);
        state = {
          pdf: pdf,
          filename: pdfSrc.filename,
          thumbnails: [],
          maxPageWidth: 0, // we work this our during initial render. See renderPdfPages.
          activePage: 1,
        };
        resolve({ success: true });
      },
      function (error) {
        console.error("Failed to load PDF. " + error);

        let reason = "unknown";
        if (error.name === "PasswordException") {
          reason = "password";
        }
        resolve({ success: false, reason: reason });
      },
    );
  });
}

export function debugGetPdf() {
  /** so debug.js has access to PDF object **/
  return state.pdf;
}

export async function pdfHasFormWidgets() {
  for (let i = 1; i <= state.pdf.numPages; i++) {
    let page = await state.pdf.getPage(i);
    let annotations = await page.getAnnotations();
    for (let a = 0; a < annotations.length; a++) {
      if (annotations[a].subtype === "Widget") {
        return true;
      }
    }
  }
  return false;
}

export async function pdfIsQwilSignTemplate() {
  const md = await state.pdf.getMetadata();
  return md.metadata && xmlContainsQwilAnchors(md.metadata.getRaw());
}

export async function initialPdfRender() {
  /** this will be the first time PDF is rendered in editor. Start with default scale so other
   * components can query the rendered pages for baseline dimensions etc
   **/
  const scale = 1.0; // important! start with 1:1 scale
  for (let i = 1; i <= state.pdf.numPages; i++) {
    let page = await state.pdf.getPage(i);
    let rendered = await initialPageRender(page, i, scale);
    state.maxPageWidth = Math.max(state.maxPageWidth, rendered.unscaledWidth);

    state.thumbnails[i] = createThumbnailImageFromRenderedCanvas(
      page,
      rendered.pageCanvas,
    );

    await renderThumbnail(state.thumbnails[i], i);
    await renderThumbnail(state.thumbnails[i], i, true); // also pre-render thumbnails for preview
  }

  fitDocumentContainerAroundPages(state.maxPageWidth, scale);

  EventBus.emit(Events.PAGE_IN_VIEW, { page: 1, screen: "editor" }); // pre-select first thumbnail.
  document.getElementById("page-counter-total-pages").textContent =
    state.pdf.numPages;
  document.getElementById("preview-page-counter-total-pages").textContent =
    state.pdf.numPages;
}

export async function renderPreview() {
  const scale = getAutofitScale("preview");

  let abandon = false;
  // subscribe to hide events, so we can abandon the task. otherwise bad things happen when
  // someone does Ninja QA and toggling previews quickly (especially with large files)
  const sub = EventBus.subscribe(Events.HIDE_PREVIEW, function () {
    abandon = true;
    sub.unsubscribe();
  });

  // reset page counter since we are rerendering the document
  EventBus.emit(Events.PAGE_IN_VIEW, { page: 1, screen: "preview" });
  resetPreviewThumbnail();

  for (let i = 1; i <= state.pdf.numPages; i++) {
    if (abandon) {
      break;
    }
    let page = await state.pdf.getPage(i);
    await initialPageRender(page, i, scale, true);

    if (abandon) {
      break;
    }

    if (i === 1) {
      fitDocumentContainerAroundPages(state.maxPageWidth, scale, true); // must be called after first page rendered
    }

    let anchors = findAnchorsInPageDiv(i);
    for (let a = 0; a < anchors.length; a++) {
      if (abandon) {
        break;
      }
      renderAnchorPreview(anchors[a], i, scale);
    }
  }

  sub.unsubscribe();

  // Trigger zoom menu updates. Should not result in a rerender since we already fit to the right scale.
  EventBus.emit(Events.ZOOM_VALUE_UPDATED, {
    zoom: "fit",
    screen: "preview",
  });
}

EventBus.subscribe(Events.SCALE_CHANGED, async function (payload) {
  await rescalePdf(payload.scale, payload.screen);
});

export async function rescalePdf(scale, screen) {
  const isPreview = screen === "preview";

  const oldScale = getCurrentScale(isPreview);

  let abandon = false;
  // subscribe to new zoom events, so we can abandon the task and let the next handler proceed
  const sub = EventBus.subscribe(Events.SCALE_CHANGED, function (payload) {
    if (payload.screen === screen) {
      abandon = true;
      sub.unsubscribe();
    }
  });
  // Also abandon rescale for preview if HIDE_PREVIEW triggered
  let sub2;
  if (isPreview) {
    sub2 = EventBus.subscribe(Events.HIDE_PREVIEW, function (payload) {
      abandon = true;
      sub2.unsubscribe();
    });
  }

  // First, track what user is currently seeing
  const viewLocation = wayfinderGetCurrentLocation(oldScale, screen);

  // Next, we resize all the anchors and containers so things are immediately usable
  for (let i = 1; i <= state.pdf.numPages; i++) {
    if (abandon) {
      console.debug("Abandoning element resize. New zoom scale requested.");
      break;
    }
    rescalePageContainer(i, scale, isPreview);
    if (i === 1) {
      fitDocumentContainerAroundPages(state.maxPageWidth, scale, isPreview);
    }
  }

  if (!abandon) {
    // scroll back to what user was seeing before page resize
    wayfinderScrollToLocation(viewLocation, scale, screen);
  }

  // then we lazily rerender each page to get the pdf rendered in the right resolution
  for (let i = 1; i <= state.pdf.numPages; i++) {
    if (abandon) {
      console.debug("Abandoning PDF rerender. New zoom scale requested.");
      break;
    }
    let page = await state.pdf.getPage(i);
    await renderPage(page, i, scale, isPreview);
  }

  sub.unsubscribe();
  if (isPreview) {
    sub2.unsubscribe();
  }
}

async function destroyIfAlreadyLoaded() {
  if (state && state.pdf) {
    await state.pdf.destroy();
    state = null;
  }
}

export async function generatePDFWithAnnotations(deleteRenderedState = true) {
  const pdf = state?.pdf;

  if (!pdf) {
    throw new Error("PDF not yet loaded");
  }

  // gather information about attached anchors and their positions
  const anchors = await collateAnchors(pdf);

  // Extract binary data of
  const pdfData = await pdf.getData();
  const oriFilename = state.filename;

  if (deleteRenderedState) {
    // free up some mem in case of large PDF
    await destroyIfAlreadyLoaded();
  }

  const pdflibDoc = await PDFDocument.load(pdfData);

  await generateAndEmbedImages(pdflibDoc, anchors); // this also updates imageRef in anchor data
  addMetadataToDocument(pdflibDoc, anchors);

  const outputPdfData = await pdflibDoc.save();
  return { data: outputPdfData, filename: convertFilename(oriFilename) };
}

function convertFilename(filename) {
  return filename.startsWith(OUTPUT_FILENAME_PREFIX)
    ? filename
    : OUTPUT_FILENAME_PREFIX + filename;
}

async function collateAnchors(pdf) {
  const collated = {};
  // find anchors by inspecting children of page divs, then add return them grouped by anchor type
  for (let i = 1; i <= pdf.numPages; i++) {
    const anchors = findAnchorsInPageDiv(i);

    for (let a = 0; a < anchors.length; a++) {
      const anchor = anchors[a];
      const key = anchor.anchor;
      if (!(anchor.anchor in collated)) {
        collated[key] = { locations: [], imageRef: "", name: key };
      }

      // canvas-based offsets (unscaled)
      const left = anchor.x;
      const top = anchor.y;
      const bottom = top + anchor.height;
      const right = left + anchor.width;

      // anchor coordinates from div would be the top-left of the rect. We want the bottom-left point instead.
      const anchorX = anchor.x;
      const anchorY = anchor.y + anchor.height;

      // map HTML coordinates to PDF coordinates
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const pdfPoint = viewport.convertToPdfPoint(anchorX, anchorY);

      collated[key].locations.push({
        page: anchor.page,
        width: anchor.width,
        height: anchor.height,
        // these are unscaled canvas pixel offset (origin top-left) for the element edges
        top: top,
        bottom: bottom,
        left: left,
        right: right,
        // these are pdf-coordinates (origin bottom-left) for the bottom left point of the element
        x: pdfPoint[0],
        y: pdfPoint[1],
      });
    }
  }

  return collated;
}

function addMetadataToDocument(pdflibDoc, anchors) {
  const xmpPayload = createXMPMetadata(anchors);

  const metadataStream = pdflibDoc.context.stream(xmpPayload, {
    Type: "Metadata",
    Subtype: "XML",
    Length: xmpPayload.length,
  });

  const metadataStreamRef = pdflibDoc.context.register(metadataStream);
  pdflibDoc.catalog.set(PDFName.of("Metadata"), metadataStreamRef);

  // also need to set metadata in doc properties
  pdflibDoc.setProducer(METADATA_PRODUCER_NAME_FULL);
  pdflibDoc.setCreator(METADATA_PRODUCER_NAME_FULL);
}

async function generateAndEmbedImages(pdflibDoc, anchors) {
  for (let key in anchors) {
    if (anchors.hasOwnProperty(key)) {
      const anchor = anchors[key];
      // N.B. we set flushCache=true just in case initial anchors created before font loaded and we cached
      // the image that used the wrong font. We only generate one image per anchor type, so no reason why we
      // have to rely on cached value.
      const anchorImageDataURI = makeAnchorImage(anchor.name, true);

      // Embed the JPG anchor image in the document
      const pdfImage = await pdflibDoc.embedPng(anchorImageDataURI);
      anchor.imageRef = pdfImage.ref.objectNumber; // capture img xref to be embedded in XML metadata

      // how display that anchor image in all locations.
      for (let i = 0; i < anchor.locations.length; i++) {
        const location = anchor.locations[i];
        let page = pdflibDoc.getPage(location.page - 1); // pdflib uses pages with 0-index

        page.drawImage(pdfImage, {
          x: location.x,
          y: location.y,
          width: location.width,
          height: location.height,
          rotate: degrees(page.getRotation().angle),
        });
      }
    }
  }
}

// track anchors dropped on page so we can efficiently add decorations to thumbnails
const assignedAnchors = {};
EventBus.subscribe(Events.ANCHOR_ADDED, function (payload) {
  handleAnchorAdded(payload.anchorId, payload.page);
});
EventBus.subscribe(Events.ANCHOR_DELETED, function (payload) {
  handleAnchorRemoved(payload.anchorId, payload.page);
});
EventBus.subscribe(Events.ANCHOR_MOVED, function (payload) {
  handleAnchorRemoved(payload.anchorId, payload.fromPage);
  handleAnchorAdded(payload.anchorId, payload.page);
});

function handleAnchorAdded(anchorId, page) {
  const anchorElement = document.getElementById(anchorId);
  const category = anchorElement.dataset.category;
  const key = `${category}-${page}`;

  const currentCount = assignedAnchors[key] || 0;
  if (currentCount) {
    assignedAnchors[key] = currentCount + 1;
  } else {
    assignedAnchors[key] = 1;
    showAnchorTabOnThumbnail(page, category);
    showAnchorTabOnThumbnail(page, category, true);
  }
}

function handleAnchorRemoved(anchorId, page) {
  const anchorElement = document.getElementById(anchorId);
  const category = anchorElement.dataset.category;
  const key = `${category}-${page}`;

  assignedAnchors[key]--;
  if (!assignedAnchors[key]) {
    hideAnchorTabOnThumbnail(page, category);
    hideAnchorTabOnThumbnail(page, category, true);
  }
}
